/**
 * Action Plan decision header (S4e, decision D2 of the approved gate prototype).
 *
 * REPLACES THE EIGHT-CARD PREAMBLE. The old header spent the first viewport on a
 * dashboard wall — eight metric cards, a FAIR breakdown and a confidence chart —
 * before the reader saw a single task. The decision header is one row that answers
 * the only first-viewport question ("what should we do first?") and moves the
 * detailed FAIR/value/confidence reporting behind a "Plan facts" disclosure:
 *
 *   - the five-state counts ARE the progress surface, each carrying the visible
 *     WORD with the number (a coloured dot with a bare count is colour-only
 *     presentation, which an aria-label does not cure for sighted users);
 *   - one honest headline figure — the plan total, or the statement that it is
 *     unavailable and why (the S4d aggregation rule: one unpriced task and there
 *     is no total);
 *   - Configure Costs and Regenerate stay visible but quiet (S4e: present, never
 *     dominating the prioritization flow).
 *
 * Every figure still goes through `actionPlanValueFormat` (P0-4). Nothing here may
 * render an absent value as zero, invent a value it was not given, or assume a
 * numeric field is present — plans arrive as stored `raw_json` documents, so
 * sparse ones are ordinary input, not a malformed edge case. The 2B contracts are
 * unchanged: counts and progress derive from the RENDERED tasks over all five
 * persisted states, an empty plan reads "No tasks" rather than 0%, and ROI/payback
 * come from the server or not at all.
 */

import React from 'react';
import { RefreshCw, Settings } from 'lucide-react';
import type { ActionPlanSummary, Phase, Task, ConfidenceDistribution } from '../types/actionPlan';
import type { DerivedFinancials, Quantified } from '../types/actionPlanValueProvenance';
import { PROVENANCE_LABEL, hasValue } from '../types/actionPlanValueProvenance';
import type { CostSummary } from './CostConfiguration/costTypes';
import {
  UNAVAILABLE_LABEL,
  barWidthPct,
  fairAverageOrNull,
  finiteOrNull,
  formatCurrency,
  formatMonths,
  formatPercent,
  formatReturn,
  formatScore,
  formatWeeks,
  safeRatioPct,
  sumTaskValues,
} from './actionPlanValueFormat';

// ============================================================================
// COMPONENT PROPS
// ============================================================================

/**
 * Nullable by declaration, because that is the truth. The previous props
 * required every number; the compiler believed it and the component crashed on
 * the first plan that disagreed.
 */
interface FairPillarScores {
  F?: number | null;
  A?: number | null;
  I?: number | null;
  R?: number | null;
}

interface ActionPlanHeaderProps {
  summary: ActionPlanSummary;
  metadata: {
    total_recommendations?: number | null;
    total_tasks?: number | null;
    total_value_usd?: Quantified<number> | null;
    total_value_forecast_usd?: Quantified<number> | null;
    fair_score_current?: FairPillarScores | null;
    fair_score_projected?: FairPillarScores | null;
    estimated_duration_weeks?: Quantified<number> | null;
  };
  /**
   * ROI and payback, computed server-side (P0-4). Optional because a plan
   * reconstructed from relational rows may arrive without it; absent means
   * unavailable, never zero.
   */
  derived?: DerivedFinancials | null;
  /**
   * REQUIRED (S4e follow-up). The rendered tasks are the ONLY source for counts,
   * progress and the scope line — the stored `summary.tasks_*` snapshot is
   * pre-overlay and was the exact staleness 2B removed. An optional prop invited
   * callers back onto that fallback.
   */
  tasks: Task[];
  /** For the eyebrow's phase count. Optional — absent phases are simply not claimed. */
  phases?: Phase[];
  costSummary?: CostSummary | null;
  onRegenerate: () => void;
  onConfigureCosts?: () => void;
  isRegenerating: boolean;
}

// ============================================================================
// UTILITY
// ============================================================================

/**
 * Calculate confidence distribution from tasks. `unpriced` is reported rather
 * than folded into a bucket: a task with no value is not a task worth $0.
 */
const calculateConfidenceDistribution = (
  tasks: Task[],
): ConfidenceDistribution & { unpriced: number } => {
  const distribution: ConfidenceDistribution & { unpriced: number } = {
    high: { count: 0, value_usd: 0 },
    medium: { count: 0, value_usd: 0 },
    low: { count: 0, value_usd: 0 },
    unpriced: 0,
  };

  tasks.forEach(task => {
    const value = finiteOrNull(task.value_usd);
    if (value === null) {
      distribution.unpriced++;
      return;
    }

    const confidence = task.value_calculation?.confidence_info?.level || 'Medium';
    switch (confidence) {
      case 'High':
        distribution.high.count++;
        distribution.high.value_usd += value;
        break;
      case 'Low':
        distribution.low.count++;
        distribution.low.value_usd += value;
        break;
      default: // Medium
        distribution.medium.count++;
        distribution.medium.value_usd += value;
    }
  });

  return distribution;
};

/** One FAIR pillar row. Extracted so the absence guards exist in one place. */
const FairPillarRow: React.FC<{
  label: string;
  current: number | null | undefined;
  projected: number | null | undefined;
}> = ({ label, current, projected }) => {
  const cur = finiteOrNull(current);
  const proj = finiteOrNull(projected);
  const delta = cur === null || proj === null ? null : proj - cur;

  return (
    <div className="fair-pillar">
      <div className="pillar-label">{label}</div>
      <div className="pillar-bar">
        <div
          className="bar-current"
          style={{ width: barWidthPct(cur, 5) }}
          title={`Current: ${formatScore(cur)}`}
        ></div>
        <div
          className="bar-projected"
          style={{ width: barWidthPct(proj, 5) }}
          title={`Projected: ${formatScore(proj)}`}
        ></div>
      </div>
      <div className="pillar-values">
        <span className="current">{formatScore(cur)}</span>
        <span className="arrow">→</span>
        <span className="projected">{formatScore(proj)}</span>
        {delta !== null && (
          <span className="improvement">
            ({delta >= 0 ? '+' : ''}{delta.toFixed(1)})
          </span>
        )}
      </div>
    </div>
  );
};

/** One fact in the Plan facts disclosure: label, value, optional note. */
const Fact: React.FC<{ label: string; value: React.ReactNode; note?: React.ReactNode }> = ({
  label,
  value,
  note,
}) => (
  <div className="ap-fact">
    <dt className="ap-fact__label">{label}</dt>
    <dd className="ap-fact__value">
      {value}
      {note && <span className="ap-fact__note">{note}</span>}
    </dd>
  </div>
);

/** The five persisted states, in display order, with their chip labels. */
const STATE_CHIPS = [
  ['not_started', 'not started'],
  ['in_progress', 'in progress'],
  ['completed', 'completed'],
  ['blocked', 'blocked'],
  ['skipped', 'skipped'],
] as const;

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const ActionPlanHeader: React.FC<ActionPlanHeaderProps> = ({
  summary,
  metadata,
  derived,
  tasks,
  phases,
  costSummary,
  onRegenerate,
  onConfigureCosts,
  isRegenerating
}) => {
  const currentAvg = fairAverageOrNull(metadata.fair_score_current);
  const projectedAvg = fairAverageOrNull(metadata.fair_score_projected);
  const fairImprovement =
    currentAvg === null || projectedAvg === null ? null : projectedAvg - currentAvg;
  // Undefined against a zero baseline — not zero, and certainly not "+Infinity%".
  const fairImprovementPct = safeRatioPct(fairImprovement, currentAvg);

  /*
   * COUNTS COME FROM THE RENDERED TASKS, EXCLUSIVELY (S4e follow-up).
   *
   * `summary.tasks_*` is a pre-overlay snapshot and omits blocked/skipped. The
   * previous revision still fell back to it for an empty rendered array — which
   * could show stale counts and progress over a list containing no tasks, the
   * exact defect 2B removed. There is NO fallback now: an empty rendered set
   * reads "No tasks" whatever the stored snapshot claims.
   *
   * FAIL CLOSED on a malformed wire status: a task whose status is outside the
   * five-state vocabulary is COUNTED — as "status unavailable", never as
   * not_started — so the chips always reconcile exactly to the rendered set.
   * Silently skipping it made the chips sum short of `totalTasks`.
   */
  const stateCounts: Record<string, number> = {
    not_started: 0, in_progress: 0, completed: 0, blocked: 0, skipped: 0,
  };
  let malformedStatusCount = 0;
  for (const t of tasks) {
    if ((t.status as string) in stateCounts) stateCounts[t.status] += 1;
    else malformedStatusCount += 1;
  }
  const completed = stateCounts.completed;
  const totalTasks = tasks.length;
  /** Recomputed from the same two numbers the fraction shows, so they cannot disagree. */
  const progressPct = totalTasks > 0 ? Math.round((completed / totalTasks) * 100) : null;

  /**
   * Cost comes from configuration or it does not come at all. `??` rather than
   * `||` so a configured zero is read as data.
   */
  const totalCost = finiteOrNull(costSummary?.total);

  // Task-level sum keeps the headline figure and the confidence bars on one number.
  const taskValues = sumTaskValues(tasks);
  const metadataValue = hasValue(metadata.total_value_usd) ? metadata.total_value_usd.value : null;
  const displayValue =
    tasks.length > 0 ? taskValues.total : (metadataValue ?? finiteOrNull(summary?.total_value_usd));

  const netValue =
    displayValue === null || totalCost === null ? null : displayValue - totalCost;

  /** ROI and payback come from the server (P0-4). Absent means unavailable — no fallback. */
  const roi = hasValue(derived?.roi_pct) ? derived.roi_pct.value : null;
  const paybackMonths = hasValue(derived?.payback_months) ? derived.payback_months.value : null;

  /** What to say under a figure whose origin is not a measurement. */
  const provenanceNote = (q: Quantified<number> | null | undefined): string | null => {
    if (!q || q.provenance === 'unavailable' || q.provenance === 'observed') return null;
    return PROVENANCE_LABEL[q.provenance];
  };

  const confidenceDistribution = calculateConfidenceDistribution(tasks);
  const totalValue = tasks.length > 0 ? taskValues.total : finiteOrNull(summary?.total_value_usd);

  const phaseCount = phases?.length ?? null;
  /** Shown only when finite — an absent count must not become "0 recommendations". */
  const recommendationCount = finiteOrNull(metadata.total_recommendations);
  const durationWeeks = hasValue(metadata.estimated_duration_weeks)
    ? metadata.estimated_duration_weeks.value
    : null;
  const eyebrow = [
    'Action Plan',
    phaseCount !== null && phaseCount > 0
      ? `${phaseCount} phase${phaseCount === 1 ? '' : 's'}`
      : null,
    durationWeeks !== null ? formatWeeks(durationWeeks) : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <header className="ap-decide-wrap">
      <div className="ap-decide">
        <div className="ap-decide__who">
          <span className="ap-decide__eyebrow">{eyebrow}</span>
          <h1>What should we do first?</h1>
          {/* Rendered count, never metadata.total_tasks — the stored figure can be
              stale against the list on screen. The recommendation count renders
              only when finite: absence is not "0 recommendations". */}
          <p className="ap-decide__scope">
            {totalTasks} task{totalTasks === 1 ? '' : 's'}
            {recommendationCount !== null && (
              <> from {recommendationCount} recommendation{recommendationCount === 1 ? '' : 's'}</>
            )}
          </p>
        </div>

        {/* The five-state counts ARE the progress surface — word + number, never a
            bare coloured dot (rev-2 gate correction #3). */}
        <div className="ap-statebar" role="group" aria-label="Task status counts">
          {totalTasks === 0 ? (
            <span className="ap-statebar__chip" data-testid="ap-progress">No tasks</span>
          ) : (
            <>
              {STATE_CHIPS.map(([state, word]) => (
                <span key={state} className="ap-statebar__chip">
                  <i className={`ap-statebar__dot ap-statebar__dot--${state}`} aria-hidden="true" />
                  {stateCounts[state]} {word}
                </span>
              ))}
              {/* Exceptional, fail-closed: a malformed wire status is counted here —
                  never mapped into not_started — so the chips reconcile exactly to
                  the rendered set. */}
              {malformedStatusCount > 0 && (
                <span className="ap-statebar__chip ap-statebar__chip--malformed">
                  {malformedStatusCount} status unavailable
                </span>
              )}
              <span className="ap-statebar__chip ap-statebar__chip--progress" data-testid="ap-progress">
                {completed} of {totalTasks} · {formatPercent(progressPct)}
              </span>
            </>
          )}
        </div>

        <div className="ap-decide__fig">
          {displayValue === null ? (
            <>
              <span className="ap-decide__fig-value ap-decide__fig-value--unavailable">
                Total value unavailable
              </span>
              <span className="ap-decide__fig-note">
                {tasks.length > 0 && taskValues.missing > 0
                  ? `${taskValues.missing} of ${tasks.length} tasks are unpriced — a partial sum is not a total`
                  : 'no value data'}
              </span>
            </>
          ) : (
            <>
              <span className="ap-decide__fig-value">{formatCurrency(displayValue)}</span>
              <span className="ap-decide__fig-note">annual forecast — not realised value</span>
            </>
          )}
        </div>

        {/* Visible, never dominant (S4e). */}
        <div className="ap-decide__actions">
          {onConfigureCosts && (
            <button className="ap-quiet-btn" type="button" onClick={onConfigureCosts}>
              <Settings size={14} aria-hidden="true" />
              Configure costs…
            </button>
          )}
          <button
            className="ap-quiet-btn"
            type="button"
            onClick={onRegenerate}
            disabled={isRegenerating}
          >
            {isRegenerating ? (
              <>
                <span className="spinner-small"></span>
                Regenerating…
              </>
            ) : (
              <>
                <RefreshCw size={14} aria-hidden="true" />
                Regenerate…
              </>
            )}
          </button>
        </div>
      </div>

      {/* Detailed reporting moves behind progressive disclosure (D2). Everything in
          here keeps the P0-4 render boundary: unavailable, never zero. */}
      <details className="ap-facts">
        <summary>Plan facts — value, FAIR, confidence, provenance</summary>
        <dl className="ap-facts__grid">
          <Fact
            label="Value"
            value={formatCurrency(displayValue)}
            note={
              displayValue === null
                ? tasks.length > 0 && taskValues.missing > 0
                  ? `${taskValues.missing} of ${tasks.length} tasks unpriced`
                  : 'no value data'
                : taskValues.missing > 0
                  ? `${taskValues.missing} of ${tasks.length} tasks unpriced`
                  : 'annual forecast'
            }
          />
          <Fact
            label="Cost"
            value={formatCurrency(totalCost)}
            note={totalCost === null ? 'not configured' : 'configured'}
          />
          <Fact label="Net Value" value={formatCurrency(netValue)} note={netValue !== null ? 'value less configured cost' : undefined} />
          <Fact
            label="ROI"
            value={formatReturn(roi)}
            note={
              roi === null
                ? (derived?.roi_pct?.basis ?? 'return on investment')
                : (provenanceNote(derived?.roi_pct) ?? 'return on investment')
            }
          />
          <Fact
            label="Payback"
            value={formatMonths(paybackMonths)}
            note={
              paybackMonths === null
                ? (derived?.payback_months?.basis ?? 'break-even')
                : (provenanceNote(derived?.payback_months) ?? 'break-even')
            }
          />
          <Fact
            label="Duration"
            value={formatWeeks(durationWeeks)}
            note={
              finiteOrNull(summary?.critical_path_length) !== null
                ? `critical: ${summary.critical_path_length}w`
                : undefined
            }
          />
          <Fact
            label="FAIR Score"
            value={`${formatScore(currentAvg)} → ${formatScore(projectedAvg)}`}
            note={
              fairImprovementPct === null
                ? 'change not calculable'
                : formatPercent(fairImprovementPct, { signed: true })
            }
          />
        </dl>

        <div className="fair-breakdown">
          <h3>FAIR Score Breakdown</h3>
          <div className="fair-pillars">
            <FairPillarRow
              label="Findable"
              current={metadata.fair_score_current?.F}
              projected={metadata.fair_score_projected?.F}
            />
            <FairPillarRow
              label="Accessible"
              current={metadata.fair_score_current?.A}
              projected={metadata.fair_score_projected?.A}
            />
            <FairPillarRow
              label="Interoperable"
              current={metadata.fair_score_current?.I}
              projected={metadata.fair_score_projected?.I}
            />
            <FairPillarRow
              label="Reusable"
              current={metadata.fair_score_current?.R}
              projected={metadata.fair_score_projected?.R}
            />
          </div>
        </div>

        {tasks.length > 0 && totalValue !== null && totalValue > 0 && (
          <div className="confidence-distribution">
            <h3>Value Confidence Distribution</h3>
            <p className="distribution-subtitle">
              How confident are we in these value estimates?
            </p>

            <div className="confidence-bar-container">
              <div className="confidence-bar">
                {(['high', 'medium', 'low'] as const).map((band) => {
                  const pct = safeRatioPct(confidenceDistribution[band].value_usd, totalValue);
                  if (pct === null || pct <= 0) return null;
                  return (
                    <div
                      key={band}
                      className={`confidence-segment ${band}`}
                      style={{ width: barWidthPct(confidenceDistribution[band].value_usd, totalValue) }}
                      title={`${band} confidence: ${formatCurrency(confidenceDistribution[band].value_usd)}`}
                    />
                  );
                })}
              </div>
            </div>

            <div className="confidence-legend">
              {(
                [
                  ['high', 'High Confidence'],
                  ['medium', 'Medium Confidence'],
                  ['low', 'Low Confidence'],
                ] as const
              ).map(([band, label]) => (
                <div key={band} className={`legend-item ${band}`}>
                  <span className="legend-dot"></span>
                  <span className="legend-label">{label}</span>
                  <span className="legend-count">{confidenceDistribution[band].count} tasks</span>
                  <span className="legend-value">
                    {formatCurrency(confidenceDistribution[band].value_usd)}
                  </span>
                  <span className="legend-pct">
                    {formatPercent(safeRatioPct(confidenceDistribution[band].value_usd, totalValue))}
                  </span>
                </div>
              ))}
              {confidenceDistribution.unpriced > 0 && (
                <div className="legend-item unpriced">
                  <span className="legend-dot"></span>
                  <span className="legend-label">No value data</span>
                  <span className="legend-count">{confidenceDistribution.unpriced} tasks</span>
                  <span className="legend-value">{UNAVAILABLE_LABEL}</span>
                  <span className="legend-pct">—</span>
                </div>
              )}
            </div>
          </div>
        )}
      </details>
    </header>
  );
};
